import React, { useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { computeAgeYears } from "../lib/ageUtils";
import { PatientLanguageSelect } from "./PatientLanguageSelect";

export const EditPatientDialog = ({ patient, onSaved, iconSize = 14, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const openDialog = (e) => {
    e?.stopPropagation?.();
    setForm({
      name: patient.name || "",
      dob: patient.dob || "",
      age: patient.age ?? "",
      sex: patient.sex || "",
      mobile: patient.mobile || "",
      alt_mobile: patient.alt_mobile || "",
      address: patient.address || "",
      occupation: patient.occupation || "",
      referred_by: patient.referred_by || "",
      preferred_language: patient.preferred_language || null,
    });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.mobile.trim()) { toast.error("Mobile is required"); return; }
    if (form.dob && form.dob > new Date().toISOString().slice(0, 10)) {
      toast.error("Date of birth cannot be in the future"); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, age: form.age === "" ? null : form.age };
      const r = await api.put(`/patients/${patient.patient_id}`, payload);
      toast.success("Patient details updated");
      setOpen(false);
      onSaved?.(r.data.patient);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update patient");
    }
    setSaving(false);
  };

  return (
    <>
      <button
        onClick={openDialog}
        title="Edit patient details"
        data-testid={`edit-patient-btn-${patient.patient_id}`}
        className={`p-1.5 rounded text-slate-400 hover:text-pink-700 hover:bg-pink-50 ${className}`}
      >
        <Pencil size={iconSize} />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="edit-patient-dialog" className="max-w-lg max-h-[90dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Edit patient — {patient.patient_id}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <PatientLanguageSelect value={form.preferred_language} onChange={(v) => set("preferred_language", v)} testId="edit-patient-language" label="Default patient language" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Full name *</Label>
              <Input data-testid="edit-patient-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Date of birth</Label>
              <Input data-testid="edit-patient-dob" type="date" value={form.dob || ""} max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => {
                  const dob = e.target.value;
                  const derived = computeAgeYears(dob);
                  setForm((f) => ({ ...f, dob, age: derived ?? f.age }));
                }} />
            </div>
            <div>
              <Label className="text-xs">Age (yrs)</Label>
              <Input data-testid="edit-patient-age" type="number" value={form.age ?? ""} onChange={(e) => set("age", e.target.value)}
                disabled={!!form.dob} title={form.dob ? "Age auto-derived from DOB" : ""} />
            </div>
            <div>
              <Label className="text-xs">Sex</Label>
              <Select value={form.sex || ""} onValueChange={(v) => set("sex", v)}>
                <SelectTrigger data-testid="edit-patient-sex"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Mobile *</Label>
              <Input data-testid="edit-patient-mobile" value={form.mobile || ""} onChange={(e) => set("mobile", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Alt. mobile</Label>
              <Input data-testid="edit-patient-alt-mobile" value={form.alt_mobile || ""} onChange={(e) => set("alt_mobile", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Occupation</Label>
              <Input data-testid="edit-patient-occupation" value={form.occupation || ""} onChange={(e) => set("occupation", e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Address</Label>
              <Input data-testid="edit-patient-address" value={form.address || ""} onChange={(e) => set("address", e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Referred by</Label>
              <Input data-testid="edit-patient-referred-by" value={form.referred_by || ""} onChange={(e) => set("referred_by", e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="edit-patient-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700" data-testid="edit-patient-save">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditPatientDialog;
